import { z } from 'zod';
import { uuidSchema } from './common';

export const activateSlotSchema = z.object({
  membershipCode: z.string().min(1).max(64),
  sponsorCode: z.string().min(1).max(64),
  binaryPosition: z.enum(['L', 'R']).optional(),
  placement: z
    .object({
      parentSlotId: uuidSchema,
      position: z.enum(['L', 'R']),
    })
    .optional(),
});

export const placeSlotSchema = z.object({
  parentSlotId: uuidSchema,
  position: z.enum(['L', 'R']),
});

/**
 * Deepest genealogy level the tree endpoint serves. The ltree walkers' cost is
 * bounded by SUBTREE SIZE, not depth (the depth filter is a plain `nlevel`
 * comparison), so this is a sanity bound, not a perf guard. It must comfortably
 * exceed any real network's depth — spillover/single-side binary chains grow
 * one level PER SLOT, so a few hundred members can legitimately sit over a
 * hundred levels deep (the old cap of 15 blocked real members' views). Both
 * SPAs' lazy-depth viewers clamp their requests to this value, so expansion
 * stops at the cap instead of 400ing.
 */
export const GENEALOGY_MAX_DEPTH = 200;

export const genealogyQuerySchema = z.object({
  view: z.enum(['unilevel', 'binary']).default('unilevel'),
  depth: z.coerce.number().int().min(1).max(GENEALOGY_MAX_DEPTH).default(5),
  rootSlotId: uuidSchema.optional(),
});

/** F2: search a slot's genealogy subtree by name / slot code / referral code. */
export const genealogySearchQuerySchema = z.object({
  q: z.string().trim().min(2).max(80),
  view: z.enum(['unilevel', 'binary']).default('unilevel'),
});

export type GenealogySearchMatch = {
  slotId: string;
  slotCode: string;
  userId: string;
  userFullName: string | null;
  userEmail: string | null;
  depth: number;
  /** Ordered slot ids from the search root down to (and including) the match. */
  pathSlotIds: string[];
};

export type GenealogySearchResult = {
  view: 'unilevel' | 'binary';
  root: string;
  matches: GenealogySearchMatch[];
};

/**
 * One slot on the tree, as `GET /slots/:id/genealogy` returns it. The binary
 * fields are absent on a unilevel read, and which of them a member may SEE is a
 * tenant setting (`BinaryGenealogyNodeFields`) applied server-side, so a client
 * must render what arrived and never assume a field is present.
 */
export type GenealogyNode = {
  slotId: string;
  slotCode: string;
  userFullName: string | null;
  userEmail: string | null;
  depth: number;
  parentSlotId: string | null;
  position?: 'L' | 'R' | null;
  membershipCode: string | null;
  membershipName: string | null;
  packageAmount: string | null;
  descendantCount: number;
  childCount: number;
  rankCode: string | null;
  leftPv?: string | null;
  rightPv?: string | null;
  sponsorSlotCode?: string | null;
  sponsorName?: string | null;
};

export type GenealogyResponse = {
  view: 'unilevel' | 'binary';
  root: string;
  depth: number;
  currency: string;
  nodes: GenealogyNode[];
};
